import {inject, Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {DateTime} from 'luxon';
import {DateTimePeriod, mapParseItem, mapParseList} from '@brunata-angular/util';
import {
    AllocationUnitStatus,
    allocationUnitStatusSchema,
    ConsumptionData,
    consumptionDataSchema,
    Interval,
} from '@brunata-angular/consumption-chart/schema';
import {environment} from 'src/environments/environment';
import {
    MeterWithReading,
    meterWithReadingSchema,
} from './consumer-profile/meters-overview/meters-overview.model';
import {
    ArchivedBill,
    archivedBillSchema,
} from './consumer-profile/bills-overview/bills-overview.model';
import {
    ArchivedOrder,
    archivedOrderSchema,
} from './consumer-profile/service-work-overview/service-work-overview.model';

@Injectable({
    providedIn: 'root',
})
export class ConsumerService {
    private readonly http = inject(HttpClient);

    updateConsumer(
        buildingNo: string,
        locationId: string,
        consumerId: string,
        update: ConsumerUpdate,
    ): Observable<unknown> {
        return this.http.put(
            `${environment.onlineServiceUrl}/buildings/${buildingNo}/locations/${locationId}/consumers/${consumerId}`,
            update,
        );
    }

    getAllocationUnitStatuses(
        buildingNo: string,
        locationId: string,
        consumerId: string,
    ): Observable<AllocationUnitStatus[]> {
        return this.http
            .get(
                `${environment.onlineServiceUrl}/buildings/${buildingNo}/locations/${locationId}/consumers/${consumerId}/allocationunitstatuses`,
            )
            .pipe(mapParseList(allocationUnitStatusSchema));
    }

    getConsumption(
        buildingNo: string,
        locationId: string,
        consumerId: string,
        period: DateTimePeriod,
        interval: Interval,
        allocationUnit: string,
    ): Observable<ConsumptionData> {
        const startDate = period.startDate.toISO();
        const endDate = period.endDate.toISO();
        if (startDate == null || endDate == null) {
            throw new Error('Invalid consumption period');
        }
        const params = new URLSearchParams({
            startdate: startDate,
            enddate: endDate,
            interval,
            allocationunit: allocationUnit,
        });
        return this.http
            .get(
                `${environment.onlineServiceUrl}/buildings/${buildingNo}/locations/${locationId}/consumers/${consumerId}/consumption?${params.toString()}`,
            )
            .pipe(mapParseItem(consumptionDataSchema));
    }

    getMeters(
        buildingNo: string,
        locationId: string,
        consumerId: string,
        readingDate: DateTime,
    ): Observable<MeterWithReading[]> {
        const startDate = readingDate.toISO();
        if (startDate == null) {
            throw new Error('Invalid reading date');
        }
        const params = new URLSearchParams({startdate: startDate});
        return this.http
            .get(
                `${environment.onlineServiceUrl}/buildings/${buildingNo}/locations/${locationId}/consumers/${consumerId}/meters?${params.toString()}`,
            )
            .pipe(mapParseList(meterWithReadingSchema));
    }

    updateMeterPlacement(
        buildingNo: string,
        locationId: string,
        consumerId: string,
        meterId: string,
        placement: string | null,
    ): Observable<unknown> {
        return this.http.put(
            `${environment.onlineServiceUrl}/buildings/${buildingNo}/locations/${locationId}/consumers/${consumerId}/meters/${meterId}`,
            {placement},
        );
    }

    getArchivedBills(
        buildingNo: string,
        locationId: string,
        consumerId: string,
    ): Observable<ArchivedBill[]> {
        return this.http
            .get(
                `${environment.onlineServiceUrl}/buildings/${buildingNo}/locations/${locationId}/consumers/${consumerId}/archive/bills`,
            )
            .pipe(mapParseList(archivedBillSchema));
    }

    getArchivedOrders(
        buildingNo: string,
        locationId: string,
        consumerId: string,
    ): Observable<ArchivedOrder[]> {
        return this.http
            .get(
                `${environment.onlineServiceUrl}/buildings/${buildingNo}/locations/${locationId}/consumers/${consumerId}/archive/orders`,
            )
            .pipe(mapParseList(archivedOrderSchema));
    }

    removeOnlineAccount(
        buildingNo: string,
        locationId: string,
        consumerId: string,
    ): Observable<unknown> {
        return this.http.delete(
            `${environment.onlineServiceUrl}/buildings/${buildingNo}/locations/${locationId}/consumers/${consumerId}/removeaccount`,
        );
    }
}

// The endpoint writes every field it is given, so a partial object clears what it leaves out.
export type ConsumerUpdate = {
    name: string;
    consumerNo: string | null;
    email: string | null;
    phoneNumber: string | null;
    isPhoneMobile: boolean | null;
};
